import { memo, useCallback, useState } from "react";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import { contractService } from "@/features/contracts/services/contract.service";
import type { ContractResponseDTO } from "@/features/contracts/types/contract.types";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { getApiErrorMessage } from "@/shared/lib/api-error";

interface ParentContractFieldProps {
  parentContractId: number | null;
  parentContractLabel: string | null;
  onSelect: (contract: ContractResponseDTO) => void;
  onClear: () => void;
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Busca de contrato pai (opcional, no máximo 1 selecionável).
 *
 * No payload da API o campo é `relatedContractsIds: number[]`, mas a
 * UI restringe a 1 — o ID viaja como array de tamanho 1 ou é omitido
 * quando vazio. Usa o mesmo `contractService.search` da picker
 * principal — sem busca por cliente, apenas por `contractNumber`.
 */
export const ParentContractField = memo(function ParentContractField({
  parentContractId,
  parentContractLabel,
  onSelect,
  onClear,
}: ParentContractFieldProps) {
  const [term, setTerm] = useState("");
  const [results, setResults] = useState<ContractResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  const fetchContracts = useCallback(
    async (rawTerm: string, signal: AbortSignal) => {
      const response = await contractService.search(
        { contractNumber: rawTerm.trim(), size: 10 },
        { signal },
      );
      setShowResults(true);
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term,
    enabled: !parentContractId,
    fetcher: fetchContracts,
    setItems: setResults,
    setLoading: setSearching,
    onError: (e) =>
      toast.error(getApiErrorMessage(e, "Erro ao buscar contratos")),
    debounceMs: SEARCH_DEBOUNCE_MS,
  });

  if (parentContractId && parentContractLabel) {
    return (
      <div className="space-y-1">
        <Label>Contrato pai (opcional)</Label>
        <div className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
          <span className="flex-1 text-sm truncate">{parentContractLabel}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClear}
            aria-label="Remover contrato pai"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="parent-contract-search">Contrato pai (opcional)</Label>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          id="parent-contract-search"
          placeholder="Busque pelo número do contrato pai"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          className="pl-8"
        />
        {searching ? (
          <Spinner className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4" />
        ) : null}
      </div>

      {showResults && results.length > 0 ? (
        <div className="rounded-md border border-input bg-background max-h-64 overflow-y-auto">
          {results.map((contract) => (
            <button
              type="button"
              key={contract.id}
              onClick={() => {
                onSelect(contract);
                setTerm("");
                setShowResults(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-accent border-b border-border last:border-b-0"
            >
              <div className="text-sm font-medium">
                Nº {contract.contractNumber}
              </div>
              <div className="text-xs text-muted-foreground">
                Cliente: {contract.clientName}
              </div>
            </button>
          ))}
        </div>
      ) : null}

      {showResults && !searching && results.length === 0 && term.length >= 1 ? (
        <p className="text-xs text-muted-foreground">
          Nenhum contrato encontrado.
        </p>
      ) : null}
    </div>
  );
});
